import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import useWindowSize from '../../hooks/useWindowSize';
import useClickOutside from '../../hooks/useClickOutside';
import { throttle } from 'lodash';
import {
  Content,
  ContentContainer,
  NavbarContainer,
  Pushable,
  SidebarPusher,
  SidebarWrapper
} from './styles';
import { Responsive, Icon } from 'semantic-ui-react';
import Navbar from '../../containers/Navbar';
import Menu from '../../containers/Menu';
import MobileMenu from '../MobileMenu';
import { iRootState, iRootDispatch } from '../../store';
import styled from 'styled-components';

const CollapseButton = styled.div<{ isCollapsed?: boolean }>`
  position: fixed;
  top: 70px;
  left: ${props => props.isCollapsed ? '54px' : '252px'};
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  background-color: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);

  &:hover {
    background-color: #f6f7f7;
    box-shadow: 0 4px 12px rgba(0,0,0,0.18);
    transform: translateY(-50%) scale(1.05);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
  
  i {
    margin: 0 !important;
  }
`;

// Container para layout mobile
const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
`;

const MobileContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

const mapState = (state: iRootState) => ({
  sidebarCollapse: state.sidebarCollapse
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  toggleSidebarCollapse: () => dispatch.sidebarCollapse.toggle()
});

type SidebarProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch> & any;

const Sidebar = (props: SidebarProps) => {
  const { isMobile } = useWindowSize();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const ref = useClickOutside<HTMLDivElement>(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const throttledHandleScroll = useCallback(
    throttle((scrollTop: number) => {
      if (scrollTop !== undefined && scrollTop !== null) {
        props.updateScroll({ posY: scrollTop });
      }
    }, 250),
    []
  );

  // Renderização para dispositivos móveis
  if (isMobile) {
    return (
      <>
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <MobileContainer>
          <NavbarContainer>
            <Navbar
              {...props}
              handleToggleSidebar={() => setIsMobileMenuOpen(true)}
              isCollapsed={false}
            />
          </NavbarContainer>
          <MobileContentContainer
            onScroll={(event: any) => {
              if (event.target) {
                throttledHandleScroll(event.target.scrollTop);
              }
            }}
            className="page-content"
          >
            <Content>{props.children}</Content>
          </MobileContentContainer>
        </MobileContainer>
      </>
    );
  }

  // Renderização para desktop (mantém comportamento original)
  return (
    <>
      <Pushable isCollapsed={props.sidebarCollapse.isCollapsed}>
        <SidebarWrapper
          animation="push"
          visible={true}
          isCollapsed={props.sidebarCollapse.isCollapsed}
        >
          <div ref={ref}>
            <Menu
              {...props}
              handleToggleSidebar={() => setIsOpen(prevValue => !prevValue)}
            />
          </div>
        </SidebarWrapper>

        <SidebarPusher>
          <Responsive fireOnMount>
            <ContentContainer
              isCollapsed={props.sidebarCollapse.isCollapsed}
              onScroll={(event: any) => {
                event.persist();

                if (event.target && event.target.className.includes('page-content')) {
                  throttledHandleScroll(event.target.scrollTop);
                }
              }}
              className={'page-content'}
            >
              <NavbarContainer>
                <Navbar
                  {...props}
                  handleToggleSidebar={() => setIsOpen(prevValue => !prevValue)}
                  isCollapsed={props.sidebarCollapse.isCollapsed}
                />
              </NavbarContainer>
              <Content>{props.children}</Content>
            </ContentContainer>
          </Responsive>
        </SidebarPusher>
      </Pushable>

      <CollapseButton
        isCollapsed={props.sidebarCollapse.isCollapsed}
        onClick={props.toggleSidebarCollapse}
      >
        <Icon name={props.sidebarCollapse.isCollapsed ? 'angle right' : 'angle left'} />
      </CollapseButton>
    </>
  );
};

export default connect(mapState, mapDispatch)(Sidebar);
